/**
 * Core types for the CRD Designer Token Studio.
 *
 * All types are immutable by design — DesignerDocument instances are never
 * mutated in place. Commands produce new document snapshots.
 */

/** Identifies a single design token within the manifest. */
export interface TokenPath {
  /** Top-level group (e.g. "colors", "typography", "spacing") */
  readonly group: string;
  /** Key within the group (e.g. "p500", "h1", "size1") */
  readonly key: string;
  /** CSS custom property name (e.g. "--bq-p500") */
  readonly cssVar: string;
}

/** A user override of a single token's value. */
export interface TokenOverride {
  readonly path: TokenPath;
  /** The new value set by the user */
  readonly value: string;
  /** The original value from the token manifest */
  readonly originalValue: string;
}

/** Immutable snapshot of all active overrides. */
export interface DesignerDocument {
  /** Map of cssVar → TokenOverride for all active overrides */
  readonly overrides: ReadonlyMap<string, TokenOverride>;
  /** Monotonically increasing version number, incremented on each edit */
  readonly version: number;
}

/** A reversible edit operation on a DesignerDocument. */
export interface EditCommand {
  /** Unique identifier for this command instance */
  readonly id: string;
  /** Human-readable description (e.g. "Set --bq-p500 to 255, 0, 0") */
  readonly description: string;
  /** Apply the edit, returning a new document snapshot. */
  apply(doc: DesignerDocument): DesignerDocument;
  /** Revert the edit, returning the previous document snapshot. */
  revert(doc: DesignerDocument): DesignerDocument;
}

/** Creates an empty DesignerDocument. */
export function createDocument(): DesignerDocument {
  return { overrides: new Map(), version: 0 };
}

/** Creates a new document with an updated overrides map and incremented version. */
export function withOverrides(
  doc: DesignerDocument,
  overrides: ReadonlyMap<string, TokenOverride>,
): DesignerDocument {
  return { overrides, version: doc.version + 1 };
}
